import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Upload, FileText, Settings2, FolderKanban, Download,
  Loader2, CheckCircle2, AlertCircle, Image as ImageIcon,
  ArrowLeft, FileCode2, BookOpen, ChevronRight, X, Plus, Clock, Eye, Pencil, Trash2,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { SNS_CODES } from '../data/snsData';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks

const CONVERSION_ICON = "https://customer-assets.emergentagent.com/job_55de5930-e1fe-4683-9c65-c6190d26c152/artifacts/tl94uv0a_Conversion.png";

const STEPS = [
  { id: 'project', label: 'Select Project', icon: FolderKanban },
  { id: 'upload', label: 'Upload Documents', icon: Upload },
  { id: 'configure', label: 'Configure', icon: Settings2 },
  { id: 'confirm', label: 'Confirmation', icon: CheckCircle2 },
  { id: 'results', label: 'Results', icon: CheckCircle2 },
];

// ──── File Status Badge ────
function StatusBadge({ status }) {
  const map = {
    pending: { color: 'bg-slate-100 text-slate-600', label: 'Pending' },
    uploading: { color: 'bg-blue-100 text-blue-700', label: 'Uploading' },
    extracting: { color: 'bg-amber-100 text-amber-700', label: 'Extracting' },
    extracted: { color: 'bg-teal-100 text-teal-700', label: 'Ready' },
    extract_failed: { color: 'bg-red-100 text-red-700', label: 'Extract Failed' },
    queued: { color: 'bg-slate-100 text-slate-600', label: 'Queued' },
    analyzing: { color: 'bg-blue-100 text-blue-700', label: 'Analyzing' },
    completed: { color: 'bg-green-100 text-green-700', label: 'Complete' },
    failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`} data-testid={`status-badge-${status}`}>{s.label}</span>;
}

export default function ConversionPage() {
  const { token, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const routeProject = location.state?.project || null;

  const [step, setStep] = useState(routeProject ? 'upload' : 'project');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(routeProject);
  const [projectId, setProjectId] = useState(routeProject?.id || '');

  // Multi-file upload state: [{id, file, jobId, status, progress, pageCount, imageCount, error}]
  const [uploadFiles, setUploadFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const uploadingRef = useRef(false);

  // Config state
  const [snsCode, setSnsCode] = useState('00');
  const [brexRef, setBrexRef] = useState('');
  const [modelIdentCode, setModelIdentCode] = useState('');
  const [extractImages, setExtractImages] = useState(true);
  const [icnMethod, setIcnMethod] = useState('model_ident');
  const [icnCageCode, setIcnCageCode] = useState('');
  const [buildPm, setBuildPm] = useState(true);
  const [selectedOriginatorIdx, setSelectedOriginatorIdx] = useState(0);

  // Batch state
  const [batchId, setBatchId] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  const [batchStatus, setBatchStatus] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Download
  const [generating, setGenerating] = useState(false);

  // Error display
  const [errorMessage, setErrorMessage] = useState(null);

  // Confirmation step state
  const [reviewSections, setReviewSections] = useState([]);
  const [infoCodes, setInfoCodes] = useState([]);
  const [schemaList, setSchemaList] = useState([]);
  const [selectedReviewIdx, setSelectedReviewIdx] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editingInfoCode, setEditingInfoCode] = useState(null);
  const [infoCodeSearch, setInfoCodeSearch] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [insertLineNum, setInsertLineNum] = useState('');

  // Fetch projects
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setProjects)
      .catch(() => {});
  }, [token]);

  // Populate settings from project
  useEffect(() => {
    if (!selectedProject) return;
    const p = selectedProject;
    if (p.model_ident_codes?.length > 0) setModelIdentCode(p.model_ident_codes[0]);
    if (p.brex_modules?.project) setBrexRef(p.brex_modules.project);
    const cap = p.capability || '';
    const capCode = cap.split(' - ')[0]?.trim();
    if (capCode && /^[0-9A-Z]{2}$/.test(capCode)) setSnsCode(capCode);
  }, [selectedProject]);

  const handleProjectSelect = (pid) => {
    setProjectId(pid);
    setSelectedProject(projects.find(p => p.id === pid) || null);
  };

  const handleProjectContinue = () => {
    if (!selectedProject) { toast.error('Please select a project'); return; }
    setStep('upload');
  };

  // ──── Chunked Upload Logic ────

  const uploadFileChunked = useCallback(async (entry) => {
    const { id, file, jobId } = entry;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    setUploadFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'uploading', progress: 0 } : f));

    try {
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('job_id', jobId);
        formData.append('chunk_index', i.toString());
        formData.append('total_chunks', totalChunks.toString());
        formData.append('filename', file.name);

        const res = await fetch(`${API}/api/conversion/upload-chunk`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error(`Chunk ${i + 1} upload failed`);

        const progress = Math.round(((i + 1) / totalChunks) * 100);
        setUploadFiles(prev => prev.map(f => f.id === id ? { ...f, progress } : f));
      }

      // Signal completion → triggers extraction
      const completeRes = await fetch(`${API}/api/conversion/upload-complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId, filename: file.name }),
      });
      if (!completeRes.ok) throw new Error('Failed to finalize upload');

      setUploadFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'extracting', progress: 100 } : f));

      // Poll extraction status
      await pollExtraction(id, jobId);
    } catch (e) {
      setUploadFiles(prev => prev.map(f => f.id === id ? { ...f, status: 'extract_failed', error: e.message } : f));
      setErrorMessage(e.message);
    }
  }, [token]);

  const pollExtraction = (fileId, jobId) => {
    return new Promise((resolve) => {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${API}/api/conversion/upload-status/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;
          const data = await res.json();

          if (data.status === 'extracted') {
            clearInterval(interval);
            setUploadFiles(prev => prev.map(f => f.id === fileId ? {
              ...f, status: 'extracted',
              pageCount: data.page_count,
              imageCount: data.image_count,
            } : f));
            resolve();
          } else if (data.status === 'extract_failed') {
            clearInterval(interval);
            setUploadFiles(prev => prev.map(f => f.id === fileId ? {
              ...f, status: 'extract_failed', error: data.error,
            } : f));
            resolve();
          }
        } catch { /* keep polling */ }
      }, 2000);
    });
  };

  // Process upload queue sequentially
  useEffect(() => {
    if (uploadingRef.current) return;
    const pending = uploadFiles.find(f => f.status === 'pending');
    if (!pending) return;

    uploadingRef.current = true;
    uploadFileChunked(pending).finally(() => {
      uploadingRef.current = false;
    });
  }, [uploadFiles, uploadFileChunked]);

  const addFiles = (fileList) => {
    const newEntries = Array.from(fileList)
      .filter(f => {
        const ext = f.name.split('.').pop().toLowerCase();
        return ['pdf', 'docx', 'doc'].includes(ext);
      })
      .map(f => ({
        id: crypto.randomUUID(),
        file: f,
        jobId: crypto.randomUUID(),
        status: 'pending',
        progress: 0,
        pageCount: 0,
        imageCount: 0,
        error: null,
      }));

    if (newEntries.length === 0) {
      toast.error('Only PDF and Word (.docx) files are supported');
      return;
    }
    setUploadFiles(prev => [...prev, ...newEntries]);
    setErrorMessage(null);
  };

  const removeFile = (id) => {
    setUploadFiles(prev => prev.filter(f => f.id !== id));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }, []);

  const allExtracted = uploadFiles.length > 0 && uploadFiles.every(f => f.status === 'extracted');
  const someUploading = uploadFiles.some(f => ['pending', 'uploading', 'extracting'].includes(f.status));
  const extractedCount = uploadFiles.filter(f => f.status === 'extracted').length;

  // ──── Batch Analysis ────
  const handleBatchAnalyze = async () => {
    const readyJobs = uploadFiles.filter(f => f.status === 'extracted');
    if (readyJobs.length === 0) { toast.error('No documents ready for analysis'); return; }

    setAnalyzing(true);
    setErrorMessage(null);

    try {
      const res = await fetch(`${API}/api/conversion/batch/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_ids: readyJobs.map(f => f.jobId),
          project_id: selectedProject?.id || '',
          sns_code: snsCode,
          brex_ref: brexRef,
          extract_images: extractImages,
          icn_method: icnMethod,
          icn_cage_code: icnCageCode,
          build_pm: buildPm,
          model_ident_code: modelIdentCode,
          selected_originator_idx: selectedOriginatorIdx,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Batch analysis failed');
      }
      const data = await res.json();
      setBatchId(data.batch_id);
      setStep('confirm');
      pollBatchStatus(data.batch_id);
    } catch (e) {
      toast.error(e.message);
      setErrorMessage(e.message);
      setAnalyzing(false);
    }
  };

  const pollBatchStatus = (bid) => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/conversion/batch/status/${bid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setBatchJobs(data.jobs || []);
        setBatchStatus(data.status);

        if (data.status === 'completed') {
          clearInterval(interval);
          setAnalyzing(false);
          const completed = data.jobs.filter(j => j.status === 'completed').length;
          toast.success(`Batch complete: ${completed}/${data.total} documents processed`);
          // Load review sections for the confirmation step
          loadReviewSections(bid);
        }
      } catch { /* keep polling */ }
    }, 3000);
  };

  // ──── Confirmation Step ────
  const loadReviewSections = async (bid) => {
    try {
      const [reviewRes, codesRes] = await Promise.all([
        fetch(`${API}/api/conversion/batch/review/${bid}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/conversion/info-codes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (reviewRes.ok) {
        const data = await reviewRes.json();
        setReviewSections(data.sections || []);
      }
      if (codesRes.ok) {
        const data = await codesRes.json();
        setInfoCodes(data.info_codes || []);
        setSchemaList(data.schemas || []);
      }
    } catch (e) {
      console.error('Failed to load review:', e);
    }
  };

  const handleReviewEdit = (sectionIdx, field, value) => {
    setReviewSections(prev => {
      const next = prev.map((s, i) => {
        if (i !== sectionIdx) return s;
        const updated = { ...s, [field]: value };
        // Auto-update info_name when info_code changes
        if (field === 'info_code') {
          const match = infoCodes.find(ic => ic.code === value);
          if (match) updated.info_name = match.name;
        }
        return updated;
      });
      // Recalculate DMCs when info_code changes (it's part of DMC string)
      if (field === 'info_code') return recalculateDMCs(next);
      return next;
    });
  };

  // Recalculate DMC codes after add/remove — mirrors backend formula
  const recalculateDMCs = (sections) => {
    const mic = modelIdentCode || '';
    const sns = snsCode || '00';
    return sections.map((sec, i) => {
      const assy = String(i % 100).padStart(2, '0');
      const subSub = String(Math.floor(i / 100) % 10);
      const sub = String(Math.floor(i / 1000) % 10);
      const ic = sec.info_code || '040';
      const dmc = `DMC-${mic}-A-${sns}-${sub}${subSub}-${assy}-00A-${ic}A-A`;
      return { ...sec, dmc, _newIdx: i };
    });
  };

  const handleRemoveSection = (idx) => {
    setReviewSections(prev => {
      const next = prev.filter((_, i) => i !== idx);
      return recalculateDMCs(next);
    });
    // Clear preview if removed row was selected
    if (selectedReviewIdx === idx) setSelectedReviewIdx(null);
    else if (selectedReviewIdx > idx) setSelectedReviewIdx(selectedReviewIdx - 1);
  };

  const handleMoveSection = (idx, direction) => {
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    setReviewSections(prev => {
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return recalculateDMCs(next);
    });
    // Track selected row through the swap
    if (selectedReviewIdx === idx) setSelectedReviewIdx(targetIdx);
    else if (selectedReviewIdx === targetIdx) setSelectedReviewIdx(idx);
  };

  const handleAddSection = (insertAt) => {
    const defaultCode = '040';
    const match = infoCodes.find(ic => ic.code === defaultCode);
    const firstJobId = reviewSections.length > 0 ? reviewSections[0].job_id : '';
    const newItem = {
      idx: -1,
      job_id: firstJobId,
      filename: '',
      dmc: '',
      info_code: defaultCode,
      info_name: match ? match.name : 'Description',
      tech_name: '',
      schema: 'descript',
      content_preview: '',
      content_type: 'description',
      level: 1,
      page_start: 0,
      _isNew: true,
    };
    setReviewSections(prev => {
      const pos = (insertAt != null && insertAt >= 0 && insertAt <= prev.length) ? insertAt : prev.length;
      const next = [...prev.slice(0, pos), newItem, ...prev.slice(pos)];
      return recalculateDMCs(next);
    });
    // Select the newly inserted row
    const pos = (insertAt != null && insertAt >= 0 && insertAt <= reviewSections.length) ? insertAt : reviewSections.length;
    setSelectedReviewIdx(pos);
    setInsertLineNum('');
  };

  const handleSaveReviewEdits = async () => {
    if (!batchId) return;
    setSavingReview(true);
    try {
      // Separate existing edits, removals (by comparing with original), and additions
      const edits = [];
      const additions = [];
      reviewSections.forEach((s) => {
        if (s._isNew) {
          additions.push({
            job_id: s.job_id,
            info_code: s.info_code,
            tech_name: s.tech_name,
            schema: s.schema,
          });
        } else {
          edits.push({
            job_id: s.job_id,
            idx: s.idx,
            info_code: s.info_code,
            tech_name: s.tech_name,
            schema: s.schema,
          });
        }
      });
      // Gather original indices that are still present (not removed)
      const survivingOriginalIdxs = reviewSections
        .filter(s => !s._isNew)
        .map(s => ({ job_id: s.job_id, idx: s.idx }));

      const res = await fetch(`${API}/api/conversion/batch/review/${batchId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ edits, additions, surviving: survivingOriginalIdxs }),
      });
      if (res.ok) {
        toast.success('Changes saved');
        setStep('results');
      } else {
        toast.error('Failed to save changes');
      }
    } catch (e) {
      toast.error('Error saving: ' + e.message);
    } finally {
      setSavingReview(false);
    }
  };

  // ──── Downloads ────
  const handleDownloadSingle = async (jobId) => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/conversion/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: jobId }),
      });
      if (!res.ok) throw new Error('Generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `S1000D_Conversion_${modelIdentCode || 'output'}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
      setErrorMessage(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!batchId) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/conversion/batch/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ batch_id: batchId }),
      });
      if (!res.ok) throw new Error('Batch generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `S1000D_Batch_${modelIdentCode || 'output'}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch (e) {
      toast.error(e.message);
      setErrorMessage(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const originators = selectedProject?.originators || [];
  const completedBatchJobs = batchJobs.filter(j => j.status === 'completed');
  const totalBatchDMs = completedBatchJobs.reduce((s, j) => s + (j.dm_count || 0), 0);
  const totalBatchICNs = completedBatchJobs.reduce((s, j) => s + (j.icn_count || 0), 0);

  return (
    <div className="space-y-6" data-testid="conversion-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(selectedProject ? `/dashboard/projects/${selectedProject.id}` : '/dashboard/tools')} data-testid="conversion-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <img src={CONVERSION_ICON} alt="Conversion" className="w-12 h-12 object-contain" />
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="conversion-title">
            Conversion Tool
          </h1>
          <p className="text-slate-500">Convert PDF/Word documents to S1000D Data Modules</p>
        </div>
        {selectedProject && (
          <div className="ml-auto flex items-center gap-2 bg-violet-50 text-violet-700 px-4 py-2 rounded-lg text-sm font-medium">
            <FolderKanban className="w-4 h-4" />
            {selectedProject.title}
            <span className="text-violet-400">({selectedProject.s1000d_version})</span>
          </div>
        )}
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2" data-testid="conversion-steps">
        {STEPS.map((s, i) => {
          const isCurrent = s.id === step;
          const isPast = STEPS.findIndex(x => x.id === step) > i;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && <ChevronRight className="w-4 h-4 text-slate-300" />}
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isCurrent ? 'bg-violet-600 text-white' : isPast ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-400'
              }`}>
                <s.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Step 1: Project Selection */}
      {step === 'project' && (
        <Card data-testid="conversion-step-project">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FolderKanban className="w-5 h-5 text-violet-600" />
              Select Project
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600">The Conversion tool requires a project. Select a project to use its settings (Model Identification Code, S1000D Issue, BREX reference, etc.)</p>
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
                <p className="text-slate-600 font-medium">No projects found</p>
                <p className="text-sm text-slate-500 mt-1">Create a project first to use the Conversion tool</p>
                <Button className="mt-4 bg-violet-600 hover:bg-violet-700" onClick={() => navigate('/dashboard/projects')}>
                  Go to Projects
                </Button>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Project</Label>
                  <Select value={projectId} onValueChange={handleProjectSelect}>
                    <SelectTrigger data-testid="conversion-project-select">
                      <SelectValue placeholder="Select a project..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.title} — {p.s1000d_version}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedProject && (
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-slate-500">S1000D Issue:</span><span className="font-medium">{selectedProject.s1000d_version}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Model Ident Code:</span><span className="font-medium">{selectedProject.model_ident_codes?.join(', ') || 'Not set'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">System Diff Code:</span><span className="font-medium">{selectedProject.system_difference_code || 'A'}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">BREX (Project):</span><span className="font-medium">{selectedProject.brex_modules?.project || 'Not set'}</span></div>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button onClick={handleProjectContinue} disabled={!selectedProject} className="bg-violet-600 hover:bg-violet-700" data-testid="conversion-project-continue">
                    Continue
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Upload Documents (Multi-File with Chunked Upload) */}
      {step === 'upload' && (
        <Card data-testid="conversion-step-upload">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Upload className="w-5 h-5 text-violet-600" />
              Upload Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                dragOver ? 'border-violet-400 bg-violet-50' : 'border-slate-300 hover:border-violet-300 hover:bg-slate-50'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById('multi-file-input')?.click()}
              data-testid="conversion-drop-zone"
            >
              <div className="flex flex-col items-center gap-2">
                <Plus className="w-10 h-10 text-slate-400" />
                <p className="text-slate-600 font-medium">Drop PDF or Word documents here</p>
                <p className="text-sm text-slate-400">or click to browse — Supports multiple files, any size</p>
              </div>
              <input
                id="multi-file-input"
                type="file"
                accept=".pdf,.docx,.doc"
                multiple
                className="hidden"
                onChange={(e) => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ''; }}
                data-testid="conversion-file-input"
              />
            </div>

            {/* File Queue */}
            {uploadFiles.length > 0 && (
              <div className="space-y-2" data-testid="conversion-file-queue">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">{uploadFiles.length} document{uploadFiles.length > 1 ? 's' : ''}</p>
                  {someUploading && (
                    <span className="text-xs text-blue-600 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Processing uploads...
                    </span>
                  )}
                </div>
                {uploadFiles.map(f => (
                  <div key={f.id} className="border rounded-lg p-3 flex items-center gap-3" data-testid={`upload-file-${f.id}`}>
                    <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-800 truncate">{f.file.name}</p>
                        <StatusBadge status={f.status} />
                      </div>
                      {f.status === 'uploading' && (
                        <div className="mt-1.5 w-full bg-slate-100 rounded-full h-1.5">
                          <div className="bg-violet-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${f.progress}%` }} />
                        </div>
                      )}
                      {f.status === 'extracting' && (
                        <p className="text-xs text-amber-600 mt-1">Extracting text and images...</p>
                      )}
                      {f.status === 'extracted' && (
                        <p className="text-xs text-teal-600 mt-1">{f.pageCount} pages, {f.imageCount} images</p>
                      )}
                      {f.error && <p className="text-xs text-red-600 mt-1 select-all">{f.error}</p>}
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{(f.file.size / (1024 * 1024)).toFixed(1)} MB</span>
                    {f.status !== 'uploading' && f.status !== 'extracting' && (
                      <button onClick={() => removeFile(f.id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`remove-file-${f.id}`}>
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Continue to Configure */}
            <div className="flex justify-between items-center pt-2">
              <p className="text-sm text-slate-500">
                {extractedCount > 0 && <span className="text-teal-600 font-medium">{extractedCount} ready</span>}
                {someUploading && <span className="text-slate-400 ml-2">(uploads in progress)</span>}
              </p>
              <Button
                onClick={() => setStep('configure')}
                disabled={extractedCount === 0}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="conversion-upload-continue"
              >
                Continue to Configure
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Configure */}
      {step === 'configure' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="conversion-step-configure">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <Settings2 className="w-5 h-5 text-violet-600" />
                Conversion Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <span className="font-medium text-slate-700">Documents to process: </span>
                <span className="text-violet-600 font-bold">{extractedCount}</span>
              </div>

              <div className="space-y-1.5">
                <Label>Model Identification Code</Label>
                <input type="text" value={modelIdentCode} onChange={(e) => setModelIdentCode(e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="e.g., S1000DBIKE" data-testid="conversion-model-ident" />
                <p className="text-xs text-slate-400">From project settings</p>
              </div>

              <div className="space-y-1.5">
                <Label>SNS Code (System Code)</Label>
                <Select value={snsCode} onValueChange={setSnsCode}>
                  <SelectTrigger data-testid="conversion-sns-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SNS_CODES.map(s => <SelectItem key={s.code} value={s.code}>{s.code} — {s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>BREX Reference</Label>
                <input type="text" value={brexRef} onChange={(e) => setBrexRef(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="BREX file name from project" data-testid="conversion-brex-ref" />
                <p className="text-xs text-slate-400">Auto-populated from project BREX settings</p>
              </div>

              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Build Publication Module</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Create PM from Table of Contents</p>
                </div>
                <Select value={buildPm ? 'yes' : 'no'} onValueChange={(v) => setBuildPm(v === 'yes')}>
                  <SelectTrigger className="w-24" data-testid="conversion-build-pm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Yes</SelectItem>
                    <SelectItem value="no">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Responsible Partner Company selector */}
              {originators.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Responsible Partner Company</Label>
                  <Select value={String(selectedOriginatorIdx)} onValueChange={(v) => setSelectedOriginatorIdx(parseInt(v, 10))}>
                    <SelectTrigger data-testid="conversion-rpc-select"><SelectValue placeholder="Select originator..." /></SelectTrigger>
                    <SelectContent>
                      {originators.map((org, i) => (
                        <SelectItem key={i} value={String(i)}>{org.company_name} — {org.cage_code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Used for PM/DM &lt;responsiblePartnerCompany&gt; and pmIssuer</p>
                </div>
              )}
              {originators.length === 1 && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <span className="font-medium text-slate-700">Responsible Partner: </span>
                  <span className="text-violet-600 font-semibold">{originators[0].company_name} — {originators[0].cage_code}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="conversion-image-config">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <ImageIcon className="w-5 h-5 text-violet-600" />
                Image Conversion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <span className="font-medium text-slate-700">Total images across all documents: </span>
                <span className="text-violet-600 font-bold">{uploadFiles.filter(f => f.status === 'extracted').reduce((s, f) => s + f.imageCount, 0)}</span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Extract Images</Label>
                  <p className="text-xs text-slate-400 mt-0.5">Extract and convert to ICNs</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{extractImages ? 'Yes' : 'No'}</span>
                  <Switch checked={extractImages} onCheckedChange={setExtractImages} data-testid="conversion-extract-images" />
                </div>
              </div>

              {extractImages && (
                <>
                  <div className="space-y-1.5">
                    <Label>ICN Numbering Method</Label>
                    <Select value={icnMethod} onValueChange={setIcnMethod}>
                      <SelectTrigger data-testid="conversion-icn-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="model_ident">Model Identification Method</SelectItem>
                        <SelectItem value="cage_code">CAGE Code Method</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {icnMethod === 'cage_code' && (
                    <div className="space-y-1.5">
                      <Label>Originator (CAGE Code)</Label>
                      {originators.length > 0 ? (
                        <Select value={icnCageCode} onValueChange={setIcnCageCode}>
                          <SelectTrigger data-testid="conversion-cage-select"><SelectValue placeholder="Select originator..." /></SelectTrigger>
                          <SelectContent>
                            {originators.map((org, i) => (
                              <SelectItem key={i} value={org.cage_code}>{org.company_name} — {org.cage_code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-sm text-amber-600 bg-amber-50 p-2 rounded">No originators configured in project settings</div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Analyze Button */}
              <div className="pt-4 border-t">
                <Button
                  onClick={handleBatchAnalyze}
                  disabled={analyzing || extractedCount === 0}
                  className="w-full bg-violet-600 hover:bg-violet-700 h-12 text-base"
                  data-testid="conversion-analyze-btn"
                >
                  {analyzing ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Analyzing {extractedCount} document{extractedCount > 1 ? 's' : ''}...</>
                  ) : (
                    <><FileCode2 className="w-5 h-5 mr-2" /> Analyze {extractedCount} Document{extractedCount > 1 ? 's' : ''}</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Confirmation — Review Analysed Sections */}
      {step === 'confirm' && (
        <div className="space-y-4">
          {analyzing ? (
            <div className="text-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-violet-600 mx-auto mb-4" />
              <p className="text-lg font-semibold text-slate-700">Analysing documents…</p>
              <p className="text-sm text-slate-500 mt-1">
                {batchJobs.filter(j => j.status === 'completed').length} / {batchJobs.length} completed
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Review Data Modules</h3>
                  <p className="text-sm text-slate-500">{reviewSections.length} data modules — review, add, or remove before generating</p>
                </div>
                <div className="flex gap-2 items-center">
                  <Button variant="outline" onClick={() => setStep('configure')} data-testid="confirm-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                  <div className="flex items-center gap-1 border border-emerald-300 rounded-md pl-1">
                    <input
                      type="number"
                      min="1"
                      max={reviewSections.length + 1}
                      placeholder={selectedReviewIdx !== null ? `${selectedReviewIdx + 2}` : '#'}
                      value={insertLineNum}
                      onChange={(e) => setInsertLineNum(e.target.value)}
                      className="w-12 text-xs text-center border-0 outline-none py-1.5 bg-transparent placeholder:text-emerald-400"
                      title="Line number to insert at (leave blank to insert after selected row, or at end)"
                      data-testid="confirm-insert-line-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const typed = insertLineNum ? parseInt(insertLineNum, 10) : null;
                        if (typed != null && !isNaN(typed)) {
                          // User typed a line number — insert before that line (1-based)
                          handleAddSection(Math.max(0, Math.min(typed - 1, reviewSections.length)));
                        } else if (selectedReviewIdx !== null) {
                          // No number typed but a row is selected — insert after it
                          handleAddSection(selectedReviewIdx + 1);
                        } else {
                          // Nothing selected, no number — append to end
                          handleAddSection(reviewSections.length);
                        }
                      }}
                      className="text-emerald-700 hover:bg-emerald-50 rounded-l-none px-2.5"
                      data-testid="confirm-add-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Line Item
                    </Button>
                  </div>
                  <Button
                    onClick={handleSaveReviewEdits}
                    disabled={savingReview}
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="confirm-generate-btn"
                  >
                    {savingReview ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Confirm & Generate
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Section Table */}
                <div className="lg:col-span-2">
                  <Card className="border-slate-200">
                    <CardContent className="p-0">
                      <div className="overflow-auto max-h-[60vh]">
                        <table className="w-full text-sm" data-testid="confirm-table">
                          <thead className="bg-slate-100 sticky top-0 z-10">
                            <tr>
                              <th className="text-center px-1 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider w-16">Order</th>
                              <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider">DMC</th>
                              <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider w-24">InfoCode</th>
                              <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider">techName / infoName</th>
                              <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider w-28">Schema</th>
                              <th className="text-center px-2 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wider w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {reviewSections.map((sec, i) => (
                              <tr
                                key={i}
                                data-testid={`confirm-row-${i}`}
                                className={`border-b border-slate-100 cursor-pointer transition-colors ${
                                  selectedReviewIdx === i ? 'bg-violet-50 border-l-2 border-l-violet-500' : sec._isNew ? 'bg-emerald-50/50 border-l-2 border-l-emerald-400 hover:bg-emerald-50' : 'hover:bg-slate-50'
                                } ${sec.level === 1 ? 'font-semibold' : sec.level >= 3 ? 'text-slate-500' : ''}`}
                                onClick={() => { setSelectedReviewIdx(i); setEditingTitle(null); setEditingInfoCode(null); }}
                              >
                                <td className="px-1 py-1 text-center">
                                  <div className="flex items-center justify-center gap-0.5">
                                    <div className="flex flex-col">
                                      <button
                                        className={`p-0 leading-none ${i === 0 ? 'text-slate-200 cursor-default' : 'text-slate-400 hover:text-violet-600'} transition-colors`}
                                        onClick={(e) => { e.stopPropagation(); if (i > 0) handleMoveSection(i, 'up'); }}
                                        disabled={i === 0}
                                        title="Move up"
                                        data-testid={`confirm-move-up-${i}`}
                                      >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        className={`p-0 leading-none ${i === reviewSections.length - 1 ? 'text-slate-200 cursor-default' : 'text-slate-400 hover:text-violet-600'} transition-colors`}
                                        onClick={(e) => { e.stopPropagation(); if (i < reviewSections.length - 1) handleMoveSection(i, 'down'); }}
                                        disabled={i === reviewSections.length - 1}
                                        title="Move down"
                                        data-testid={`confirm-move-down-${i}`}
                                      >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <span className="text-slate-400 text-xs w-5 text-center">{i + 1}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2 font-mono text-[11px] text-slate-600 max-w-[180px] truncate" title={sec.dmc}>{sec.dmc}</td>
                                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                  {editingInfoCode === i ? (
                                    <div className="relative">
                                      <input
                                        autoFocus
                                        className="w-full border border-violet-300 rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-violet-400"
                                        placeholder="Search code…"
                                        value={infoCodeSearch}
                                        onChange={(e) => setInfoCodeSearch(e.target.value)}
                                        onBlur={() => { setTimeout(() => setEditingInfoCode(prev => prev === i ? null : prev), 200); }}
                                        data-testid={`confirm-infocode-search-${i}`}
                                      />
                                      <div className="absolute z-50 top-full left-0 mt-0.5 w-64 max-h-48 overflow-auto bg-white border border-slate-200 rounded shadow-lg text-xs">
                                        {infoCodes
                                          .filter(ic => !infoCodeSearch || ic.code.toLowerCase().includes(infoCodeSearch.toLowerCase()) || ic.name.toLowerCase().includes(infoCodeSearch.toLowerCase()))
                                          .slice(0, 50)
                                          .map(ic => (
                                            <div
                                              key={ic.code}
                                              className={`px-2 py-1 cursor-pointer hover:bg-violet-50 ${ic.code === sec.info_code ? 'bg-violet-100 font-medium' : ''}`}
                                              onMouseDown={(e) => { e.preventDefault(); handleReviewEdit(i, 'info_code', ic.code); setEditingInfoCode(null); setInfoCodeSearch(''); }}
                                            >
                                              <span className="font-mono">{ic.code}</span> – {ic.name}
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className="cursor-pointer px-1.5 py-1 border border-transparent hover:border-slate-200 rounded text-xs"
                                      onClick={() => { setEditingInfoCode(i); setInfoCodeSearch(''); }}
                                      title={`${sec.info_code} – Click to change`}
                                      data-testid={`confirm-infocode-${i}`}
                                    >
                                      <span className="font-mono">{sec.info_code}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                  {editingTitle === i ? (
                                    <input
                                      autoFocus
                                      className="w-full border border-violet-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-violet-400"
                                      value={sec.tech_name}
                                      onChange={(e) => handleReviewEdit(i, 'tech_name', e.target.value)}
                                      onBlur={() => { setTimeout(() => setEditingTitle(prev => prev === i ? null : prev), 150); }}
                                      onKeyDown={(e) => e.key === 'Enter' && setEditingTitle(null)}
                                      data-testid={`confirm-title-input-${i}`}
                                    />
                                  ) : (
                                    <div
                                      className="flex items-center gap-1.5 cursor-text group"
                                      onClick={() => setEditingTitle(i)}
                                      title="Click to edit"
                                    >
                                      <span className="truncate max-w-[200px]" title={sec.tech_name}>
                                        {sec.tech_name || <span className="text-slate-300 italic">Enter title…</span>}
                                      </span>
                                      <span className="text-slate-400 text-[10px]">– {sec.info_name}</span>
                                      <Pencil className="w-3 h-3 text-slate-300 group-hover:text-violet-500 transition-colors flex-shrink-0" />
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                  <select
                                    value={sec.schema}
                                    onChange={(e) => handleReviewEdit(i, 'schema', e.target.value)}
                                    className="w-full border border-slate-200 rounded px-1.5 py-1 text-xs bg-white focus:ring-1 focus:ring-violet-400"
                                    data-testid={`confirm-schema-${i}`}
                                  >
                                    {schemaList.map(s => (
                                      <option key={s} value={s}>{s}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <button
                                    className="text-slate-300 hover:text-red-500 transition-colors p-0.5 rounded hover:bg-red-50"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveSection(i); }}
                                    title="Remove this data module"
                                    data-testid={`confirm-remove-${i}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Content Preview Panel */}
                <div className="lg:col-span-1">
                  <Card className="border-slate-200 sticky top-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Eye className="w-4 h-4 text-violet-600" />
                        Source Content Preview
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedReviewIdx !== null && reviewSections[selectedReviewIdx] ? (
                        <div>
                          <div className="mb-3 pb-2 border-b border-slate-100">
                            <p className="text-xs text-slate-500">
                              <span className="font-semibold">File:</span> {reviewSections[selectedReviewIdx].filename}
                            </p>
                            <p className="text-xs text-slate-500">
                              <span className="font-semibold">Page:</span> {reviewSections[selectedReviewIdx].page_start + 1}
                            </p>
                            <p className="text-xs text-slate-500">
                              <span className="font-semibold">Type:</span> {reviewSections[selectedReviewIdx].content_type}
                            </p>
                          </div>
                          <div className="bg-slate-50 rounded p-3 max-h-[45vh] overflow-auto">
                            <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono leading-relaxed" data-testid="confirm-preview-content">
                              {reviewSections[selectedReviewIdx].content_preview || '(no content)'}
                            </pre>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <Eye className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                          <p className="text-sm text-slate-400">Select a row to preview source content</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 5: Results — Batch Queue Dashboard */}
      {step === 'results' && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-5 text-center">
                <FileText className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-slate-700" data-testid="batch-doc-count">{batchJobs.length}</p>
                <p className="text-sm text-slate-500">Documents</p>
              </CardContent>
            </Card>
            <Card className="bg-violet-50 border-violet-200">
              <CardContent className="p-5 text-center">
                <FileCode2 className="w-8 h-8 text-violet-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-violet-700" data-testid="batch-dm-count">{totalBatchDMs}</p>
                <p className="text-sm text-violet-500">Data Modules</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-5 text-center">
                <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-blue-700" data-testid="batch-pm-count">{completedBatchJobs.length}</p>
                <p className="text-sm text-blue-500">Publication Modules</p>
              </CardContent>
            </Card>
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-5 text-center">
                <ImageIcon className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-700" data-testid="batch-icn-count">{totalBatchICNs}</p>
                <p className="text-sm text-emerald-500">ICNs (Images)</p>
              </CardContent>
            </Card>
          </div>

          {/* Job Queue Table */}
          <Card data-testid="batch-job-table">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {analyzing ? <Loader2 className="w-5 h-5 animate-spin text-violet-600" /> : <CheckCircle2 className="w-5 h-5 text-violet-600" />}
                Document Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3 font-medium text-slate-600">#</th>
                      <th className="text-left p-3 font-medium text-slate-600">Document</th>
                      <th className="text-left p-3 font-medium text-slate-600">Status</th>
                      <th className="text-left p-3 font-medium text-slate-600">DMs</th>
                      <th className="text-left p-3 font-medium text-slate-600">ICNs</th>
                      <th className="text-left p-3 font-medium text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batchJobs.map((job, i) => (
                      <tr key={job.job_id} className="border-b hover:bg-slate-50" data-testid={`batch-job-row-${i}`}>
                        <td className="p-3 text-slate-400">{i + 1}</td>
                        <td className="p-3 font-medium text-slate-800">{job.filename}</td>
                        <td className="p-3">
                          <StatusBadge status={job.status} />
                          {job.status === 'analyzing' && <Loader2 className="w-3 h-3 animate-spin inline ml-1 text-blue-500" />}
                          {job.error && <p className="text-xs text-red-500 mt-1 select-all">{job.error}</p>}
                        </td>
                        <td className="p-3 text-slate-600">{job.dm_count || '-'}</td>
                        <td className="p-3 text-slate-600">{job.icn_count || '-'}</td>
                        <td className="p-3">
                          {job.status === 'completed' && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
                              onClick={() => handleDownloadSingle(job.job_id)} disabled={generating}
                              data-testid={`download-single-${i}`}
                            >
                              <Download className="w-3 h-3 mr-1" /> ZIP
                            </Button>
                          )}
                          {job.status === 'queued' && <Clock className="w-4 h-4 text-slate-400" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Download All */}
          <div className="flex justify-center gap-4 pt-4">
            <Button variant="outline" onClick={() => { setStep('configure'); setBatchId(null); setBatchJobs([]); setBatchStatus(null); }}
              data-testid="conversion-reconfigure-btn">
              Reconfigure
            </Button>
            {completedBatchJobs.length > 1 && (
              <Button onClick={handleDownloadAll} disabled={generating}
                className="bg-violet-600 hover:bg-violet-700 h-12 px-8 text-base" data-testid="conversion-download-all-btn">
                {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</> : <><Download className="w-5 h-5 mr-2" /> Download All ({completedBatchJobs.length} documents)</>}
              </Button>
            )}
            {completedBatchJobs.length === 1 && (
              <Button onClick={() => handleDownloadSingle(completedBatchJobs[0].job_id)} disabled={generating}
                className="bg-violet-600 hover:bg-violet-700 h-12 px-8 text-base" data-testid="conversion-download-btn">
                {generating ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating...</> : <><Download className="w-5 h-5 mr-2" /> Download S1000D Package</>}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Persistent Error Display */}
      {errorMessage && (
        <div className="mt-4 border border-red-200 bg-red-50 rounded-lg p-4" data-testid="conversion-error-display">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2 min-w-0">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 select-all break-all" data-testid="conversion-error-text">{errorMessage}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-100"
                onClick={() => { navigator.clipboard.writeText(errorMessage); toast.success('Error copied to clipboard'); }}
                data-testid="conversion-error-copy-btn">
                Copy
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-400 hover:text-red-600 hover:bg-red-100"
                onClick={() => setErrorMessage(null)} data-testid="conversion-error-dismiss-btn">
                ✕
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
